import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PushPinIcon from '@mui/icons-material/PushPin';
import CloseIcon from '@mui/icons-material/Close';

import { RadioButtons } from 'handimap-react-lib/components/PoiListingMenuComponents/RadioButtons';
import { FeatureListingResult } from 'handimap-react-lib/components/PoiListingMenuComponents/FeatureListingResult';
import { callPostSearchQuery } from 'handimap-react-lib/api/jsonFeatureApi';
import { GeocoderInput } from '../components/GeocoderInput';
import { hideAlert } from 'handimap-react-lib/store/actions/alertActions';
import { closePoiListingMenu } from 'handimap-react-lib/store/actions/poiListingActions';

const TIME_OUT_THRESHOLD = 800; // ms
const distanceLabels = ['100 m', '500 m', '1 km', '2 km', '3 km'];
const distanceValues = [100, 500, 1000, 2000, 3000];

export const PoiListingMenu = ({ isMobile }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();

  const buttonsLayers = useSelector(
    (state) => state.layerConf.buttonsLayersState,
  );
  const queriableLayers = useSelector(
    (state) => state.layerConf.getFeatureInfoAttrs,
  );
  const showAlert = useSelector((state) => state.alert.isOpen);

  const [distance, setDistance] = useState(100);
  const [theme, setTheme] = useState(null);
  const [layersForTheme, setLayersForTheme] = useState(null);
  const [addressCoords, setAddressCoords] = useState([]);
  const [layerList, setLayerList] = useState([]);
  const [layerLabelList, setLayerLabelList] = useState([]);
  const [layerIconList, setLayerIconList] = useState([]);
  const [poiList, setPoiList] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const handlePoiList = (layersList) => {
    if (addressCoords.length > 0 && theme !== null) {
      setIsLoading(true);
      callPostSearchQuery(
        layersList,
        addressCoords[0],
        addressCoords[1],
        distance,
      )
        .then((result) => {
          const { query, time } = result;
          if (TIME_OUT_THRESHOLD - time > 0) {
            /**
             * if time execution is shorter than 800 then setTimeout at 800
             */
            setTimeout(() => {
              query.then((poiResult) => {
                setPoiList(poiResult);
                setIsLoading(false);
              });
            }, TIME_OUT_THRESHOLD);
          } else {
            /**
             * if time execution is longer than 800 then set result immediately
             */
            query.then((poiResult) => {
              setPoiList(poiResult);
              setIsLoading(false);
            });
          }
        })
        .catch((error) => {
          console.error(error.message);
          setPoiList([]);
          setIsLoading(false);
        });
    }
  };

  const closePoiListing = () => {
    dispatch(closePoiListingMenu());
  };

  useEffect(() => {
    var layers = [];
    var labels = [];
    var icons = [];
    for (const [layer, obj] of Object.entries(buttonsLayers)) {
      layers.push(layer);
      labels.push(obj.label);
      icons.push(obj.blackPngIcon);
    }
    setLayerList(layers);
    setLayerLabelList(labels);
    setLayerIconList(icons);
    setTheme(layers[0]);
  }, [buttonsLayers]);

  useEffect(() => {
    if (theme !== null && theme !== undefined) {
      const layersForTheme = buttonsLayers[theme].layersName.filter((layer) => {
        if (Object.keys(queriableLayers).includes(layer)) {
          return true;
        } else {
          return false;
        }
      });
      handlePoiList(layersForTheme);
      setLayersForTheme(layersForTheme);
    }
  }, [theme]);

  useEffect(() => {
    if (showAlert) {
      dispatch(hideAlert());
    }
    handlePoiList(layersForTheme);
  }, [distance, addressCoords, i18n]);

  const styles = {
    container: {
      boxSizing: 'border-box',
      width: isMobile ? '100%' : 420,
      backgroundColor: '#FFFFFF',
      height: '100%',
      paddingX: '1em',
      paddingY: '1em',
    },
    closeContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
    },
    infoContainer: {
      marginTop: '2em',
      paddingX: '1em',
    },
    inputContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    titleContainer: {
      display: 'flex',
    },
    title: {
      color: '#000000',
      fontSize: '1.4em',
    },
    labelSection: {
      color: '#000000',
      fontSize: '1em',
    },
    categoryContainer: {
      display: 'flex',
    },
  };
  return (
    <Box id='poi-search-container' sx={styles.container}>
      <Box sx={styles.closeContainer}>
        <IconButton
          id='poi_listing_close'
          aria-controls='poi-disclosure'
          aria-label={t('poiListing.map')}
          onClick={closePoiListing}
          tooltip={t('poiListing.map')}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Stack sx={styles.infoContainer} direction='column' spacing={2.5}>
        <Box sx={styles.titleContainer}>
          <Typography component='h1' sx={styles.title}>
            {t('poiListing.menu-main-title')}
          </Typography>
        </Box>
        <Stack>
          <InputLabel
            id='menu-theme-title'
            role='presentation'
            style={styles.labelSection}
          >
            {t('layer.layer-label')}
          </InputLabel>
          <RadioButtons
            valueList={layerList}
            labelList={layerLabelList}
            iconList={layerIconList}
            labelledby='menu-theme-title'
            callbackFunction={(value) => setTheme(value)}
          />
        </Stack>
        <Stack sx={styles.categoryContainer} spacing={1}>
          <InputLabel htmlFor='search-poi' sx={styles.labelSection}>
            {t('poiListing.menu-address-title')}
          </InputLabel>
          <GeocoderInput
            id='search-poi'
            placeholder={t('poiListing.search-placeholder')}
            onComplete={(label, center) => setAddressCoords(center)}
            inputValue=''
          />
        </Stack>
        <Stack>
          <InputLabel
            id='menu-distance-title'
            role='presentation'
            sx={styles.labelSection}
          >
            {t('poiListing.menu-distance-title')}
          </InputLabel>
          <RadioButtons
            valueList={distanceValues}
            labelList={distanceLabels}
            labelledby='menu-distance-title'
            callbackFunction={(value) => setDistance(value)}
          />
        </Stack>
        {addressCoords.length > 0 && (
          <>
            <Stack direction='row' spacing={1} style={{ marginTop: '2em' }}>
              <PushPinIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
              <Typography
                component='h2'
                style={{
                  fontSize: isMobile ? '1em' : '1.5em',
                  fontFamily: 'Luciole-Bold',
                }}
              >
                {t('poiListing.menu-listing-title')}
              </Typography>
            </Stack>
            <Box sx={{ paddingBottom: '2em' }}>
              {isLoading && <LinearProgress />}
              {!isLoading && (
                <FeatureListingResult poiList={poiList} isMobile={isMobile} />
              )}
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};
